import React from 'react';
import { ICmriOverviewSidebarFilters, useCmriOverviewSidebarFiltersContext } from './CmriOverviewSidebarFiltersContext';
import { CollapseFilterWrapper } from 'src/components/sidebar-filters/CollapseFilterWrapper';
import { MarketAwardSourceFilterSelect } from 'src/components/sidebar-filters/filter-select/MarketAwardSourceFilterSelect';
import { useSidebarFilterOptionsContext } from 'src/pages/energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { MarketAwardStatusFilterSelect } from 'src/components/sidebar-filters/filter-select/MarketAwardStatusFilterSelect';
import { CaisoResourceFilterSelect } from 'src/components/sidebar-filters/filter-select/CaisoResourceFilterSelect';
import { BaseFilterSlider } from 'src/components/sidebar-filters/base/BaseFilterSlider';
import { currencyUSDFormatter } from 'src/domain/common/currencyFormatters';
import { BaseFilterAccordion } from 'src/components/sidebar-filters/base/BaseFilterAccordion';
import _ from 'lodash';

export const CmriOverviewFilterKeys = {
    resource: 'resource',

    // Filters
    source: 'source',
    status: 'status',

    // Thresholds
    hoursRange: 'hoursRange',
    mw: 'mw',
    price: 'price',
    earnings: 'earnings',
} as const;

export type TCmriOverviewFilterKey = (typeof CmriOverviewFilterKeys)[keyof typeof CmriOverviewFilterKeys];

export const CmriOverviewFilterLabels = {
    [CmriOverviewFilterKeys.source]: 'Source',
    [CmriOverviewFilterKeys.status]: 'Status',
    [CmriOverviewFilterKeys.resource]: 'Resource',
    [CmriOverviewFilterKeys.hoursRange]: 'Start/End',
    [CmriOverviewFilterKeys.mw]: 'MW',
    [CmriOverviewFilterKeys.price]: 'Price',
    [CmriOverviewFilterKeys.earnings]: 'Earnings',
} as const;

interface ICmriOverviewSidebarFiltersProps {
    onFilterChange: (filters: ICmriOverviewSidebarFilters) => void;
}

export const CmriOverviewSidebarFilters: React.FC<ICmriOverviewSidebarFiltersProps> = ({ onFilterChange }) => {
    const { filters, handleFilterChange, handleFiltersChange } = useCmriOverviewSidebarFiltersContext();
    const {
        isLoading: isFiltersLoading,
        marketAwardSourceOptions,
        marketAwardStatusOptions,
        caisoResourceOptions,
    } = useSidebarFilterOptionsContext();

    console.info('CmriOverviewSidebarFilters filters', filters);
    const handleCmriOverviewFilterChange = (key: string, value: unknown) => {
        console.info('CmriOverviewSidebarFilters onChange', value);
        handleFilterChange(key, value);
        onFilterChange({ ...filters, [key]: value });
    };

    function handleThresholdClear() {
        const updatedFilters = { ...filters };
        updatedFilters['hoursRange'] = undefined;
        updatedFilters['mw'] = undefined;
        updatedFilters['price'] = undefined;
        updatedFilters['earnings'] = undefined;

        handleFiltersChange(updatedFilters);

        onFilterChange({
            ...filters,
            mw: undefined,
            hoursRange: undefined,
            price: undefined,
            earnings: undefined,
        });
    }

    function areThresholdValuesChanged() {
        const { mw, hoursRange, price, earnings } = filters;
        const hoursRangeChanged = _.isArray(hoursRange) && (hoursRange[0] !== 1 || hoursRange[1] !== 24);
        const mwChanged = _.isArray(mw) && (mw[0] !== 0 || mw[1] !== 10);
        const priceChanged = _.isArray(price) && (price[0] !== 0 || price[1] !== 1000);
        const earningsChanged = _.isArray(earnings) && (earnings[0] !== 0 || earnings[1] !== 10000);

        return mwChanged || hoursRangeChanged || priceChanged || earningsChanged ? true : null;
    }

    let isThresholdHasValue = areThresholdValuesChanged();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
            <CollapseFilterWrapper label="Filters">
                <CaisoResourceFilterSelect
                    label={CmriOverviewFilterLabels[CmriOverviewFilterKeys.resource]}
                    value={filters[CmriOverviewFilterKeys.resource]}
                    onChange={value => handleFilterChange(CmriOverviewFilterKeys.resource, value)}
                    isLoading={isFiltersLoading}
                    options={caisoResourceOptions}
                />
            </CollapseFilterWrapper>
            <CollapseFilterWrapper label="Options">
                <MarketAwardSourceFilterSelect
                    height={120}
                    label={CmriOverviewFilterLabels[CmriOverviewFilterKeys.source]}
                    options={marketAwardSourceOptions}
                    value={filters[CmriOverviewFilterKeys.source]}
                    onChange={value => handleCmriOverviewFilterChange(CmriOverviewFilterKeys.source, value)}
                />
                <MarketAwardStatusFilterSelect
                    height={200}
                    label={CmriOverviewFilterLabels[CmriOverviewFilterKeys.status]}
                    options={marketAwardStatusOptions}
                    value={filters[CmriOverviewFilterKeys.status]}
                    onChange={value => handleCmriOverviewFilterChange(CmriOverviewFilterKeys.status, value)}
                />
                <BaseFilterAccordion label="Thresholds" value={isThresholdHasValue} onClear={handleThresholdClear} showClear={true}>
                    <BaseFilterSlider
                        label="Start/End"
                        min={1}
                        max={24}
                        defaultValue={[1, 24]}
                        value={filters[CmriOverviewFilterKeys.hoursRange]}
                        valueFormatter={(value?: number) => (value != undefined ? `HE${value}` : '')}
                        onChange={value => handleCmriOverviewFilterChange(CmriOverviewFilterKeys.hoursRange, value)}
                    />
                    <BaseFilterSlider
                        label="MW"
                        min={0}
                        max={10}
                        defaultValue={[0, 10]}
                        value={filters[CmriOverviewFilterKeys.mw]}
                        valueFormatter={(value?: number) => (value != undefined ? `${value}MW` : '')}
                        onChange={value => handleCmriOverviewFilterChange(CmriOverviewFilterKeys.mw, value)}
                    />
                    <BaseFilterSlider
                        label="Price"
                        min={0}
                        max={1000}
                        defaultValue={[0, 1000]}
                        value={filters[CmriOverviewFilterKeys.price]}
                        valueFormatter={(value?: number) => (value != undefined ? currencyUSDFormatter.format(value) : '')}
                        onChange={value => handleCmriOverviewFilterChange(CmriOverviewFilterKeys.price, value)}
                    />
                    <BaseFilterSlider
                        label="Earnings"
                        min={0}
                        max={10000}
                        defaultValue={[0, 10000]}
                        value={filters[CmriOverviewFilterKeys.earnings]}
                        valueFormatter={(value?: number) => (value != undefined ? currencyUSDFormatter.format(value) : '')}
                        onChange={value => handleCmriOverviewFilterChange(CmriOverviewFilterKeys.earnings, value)}
                    />
                </BaseFilterAccordion>
            </CollapseFilterWrapper>
        </div>
    );
};
